import { Box, Center, Flex, Grid, Image, Spinner, useDisclosure } from '@chakra-ui/react'
import { Button as UbiButton, Text as UbiText, Column } from '@ubirider/pick-component-library'
import { useTranslation } from 'react-i18next'
import { motion, AnimatePresence } from 'motion/react'

import { NewBillingModal } from '../../modals/NewBillingModal/NewBillingModal'
import { BillingProfile } from './components/BillingProfile'

import emptyStateIllustration from '../../assets/illustration_billing.svg'
import plusIcon from '../../assets/plus_white.svg'
import { CardsMenu } from './components/CardsMenu'
import { useBilling, useCards } from '../../context/DataProvider'

const MFlex = motion.create(Flex)
const MBox = motion.create(Box)
const MGrid = motion.create(Grid)
const MCenter = motion.create(Center)
const MColumn = motion.create(Column)
const MUbiText = motion.create(UbiText)

const variants = {
  container: {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { when: 'beforeChildren', staggerChildren: 0.08, delayChildren: 0.05 }
    },
    exit: { opacity: 0, transition: { duration: 0.15 } }
  },
  section: {
    hidden: { opacity: 0, y: 10 },
    visible: { opacity: 1, y: 0, transition: { type: 'spring', stiffness: 360, damping: 26 } },
    exit: { opacity: 0, y: 6, transition: { duration: 0.15 } }
  },
  header: {
    hidden: { opacity: 0, y: 8 },
    visible: { opacity: 1, y: 0, transition: { type: 'spring', stiffness: 360, damping: 28 } },
    exit: { opacity: 0, y: 6, transition: { duration: 0.15 } }
  },
  card: {
    hidden: { opacity: 0, y: 10, scale: 0.98 },
    visible: { opacity: 1, y: 0, scale: 1, transition: { type: 'spring', stiffness: 360, damping: 26 } },
    exit: { opacity: 0, y: 6, scale: 0.98, transition: { duration: 0.15 } }
  },
  buttonLate: {
    hidden: { opacity: 0, y: 8 },
    visible: { opacity: 1, y: 0, transition: { type: 'spring', stiffness: 360, damping: 26, delay: 0.25 } }
  }
}

export const Wallet = () => {
  const { t } = useTranslation()

  const { isOpen, onOpen, onClose } = useDisclosure()

  const { cards, loading: cardsLoading } = useCards()
  const { billing, loading: billingLoading } = useBilling()
  const loading = cardsLoading || billingLoading

  return (
    <MFlex direction='column' gap='20px' flex='1' initial='hidden' animate='visible' exit='exit' variants={variants.container}>
      <MBox variants={variants.section}>
        <CardsMenu cards={cards.filter(card => !card.pick)} loading={loading} />
      </MBox>

      <MBox flex='1' variants={variants.section}>
        <MFlex
          bg={{ sm: '', md: 'neutral.100', lg: 'neutral.100', xl: 'neutral.100' }}
          rounded='8px'
          padding={{ sm: '', md: '24px', lg: '24px', xl: '24px' }}
          variants={variants.container}
        >
          <MColumn css={{ gap: '16px', flex: '1' }} variants={variants.container}>
            <MUbiText type='displayXS' weight='bold' variants={variants.header}>
              {t('billing_information.billing_information')}
            </MUbiText>

            <AnimatePresence mode='wait'>
              {loading ? (
                <MCenter key='loading' minH='240px' variants={variants.card}>
                  <Spinner thickness='6px' color='neutral.500' size='xl' />
                </MCenter>
              ) : billing != null && billing.length > 0 ? (
                <MColumn key='content' css={{ gap: '24px' }} variants={variants.container}>
                  <MUbiText type='textSM' variants={variants.header}>
                    {t('billing_information.billing_information_description')}
                  </MUbiText>

                  <MFlex variants={variants.header}>
                    <motion.div variants={variants.buttonLate}>
                      <UbiButton
                        onClick={onOpen}
                        style='ghost'
                        type='primary'
                        leftIconProps={{ src: plusIcon }}
                        label={t('billing_information.add_new_billing_information')}
                        css={{ padding: '16px 4px' }}
                      />
                    </motion.div>
                  </MFlex>

                  <MGrid
                    variants={variants.container}
                    templateColumns={{
                      sm: 'repeat(1, 382px)',
                      md: 'repeat(2, 382px)',
                      lg: 'repeat(2, 382px)',
                      xl: 'repeat(2, 382px)',
                      xxl: 'repeat(2, 382px)'
                    }}
                    gap={{ sm: '24px', md: '16px', lg: '16px', xl: '16px' }}
                  >
                    {billing.map((data, id) => (
                      <motion.div key={data?.id ?? id} variants={variants.card}>
                        <BillingProfile id={id} info={data} />
                      </motion.div>
                    ))}
                  </MGrid>
                </MColumn>
              ) : (
                <MFlex
                  key='empty'
                  direction='column'
                  my={{ sm: '', md: '48px', lg: '48px', xl: '48px' }}
                  mx={{ sm: '', md: '96px', lg: '96px', xl: '96px' }}
                  justifyContent='center'
                  alignItems='center'
                  textAlign='center'
                  gap='24px'
                  variants={variants.card}
                >
                  <motion.div variants={variants.header}>
                    <Image src={emptyStateIllustration} height='112px' />
                  </motion.div>

                  <MUbiText type='textSM' css={{ textAlign: 'center' }} variants={variants.header}>
                    {t('billing_information.billing_information_description')}
                  </MUbiText>

                  <Center>
                    <motion.div variants={variants.buttonLate}>
                      <UbiButton
                        onClick={onOpen}
                        style='solid'
                        type='primary'
                        leftIconProps={{ src: plusIcon }}
                        label={t('billing_information.add_new_billing_information')}
                      />
                    </motion.div>
                  </Center>
                </MFlex>
              )}
            </AnimatePresence>
          </MColumn>

          <NewBillingModal onOpen={onOpen} onClose={onClose} isOpen={isOpen} />
        </MFlex>
      </MBox>
    </MFlex>
  )
}